import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  BarChart3, Bot, TrendingUp, History, Wallet,
  ArrowLeftRight, LogOut, Zap, ShieldAlert, ChevronRight,
  Activity, DollarSign, ShieldCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Nav items per sub_role
const NAV_BY_ROLE = {
  CLIENT: [
    { path: '/trade/volume-bot',    label: 'Volume Bot',     icon: Bot,          color: '#E8520F' },
    { path: '/trade/lure-bot',      label: 'Lure / Sniper',  icon: Zap,          color: '#f97316' },
    { path: '/trade/token-safety',  label: 'Token Safety',   icon: ShieldCheck,  color: '#10B981' },
    { path: '/trade/earnings',      label: 'Bot Earnings',   icon: DollarSign,   color: '#10B981' },
    { path: '/trade/buy-credits',   label: 'Buy Credits',    icon: Zap,          color: '#D4A017' },
    { path: '/trade/analytics',     label: 'Analytics',      icon: BarChart3,    color: '#D4A017' },
    { path: '/trade/portfolio',     label: 'Portfolio',      icon: TrendingUp,   color: '#06b6d4' },
    { path: '/trade/exchanges',     label: 'DeFi Exchanges', icon: ArrowLeftRight, color: '#8b5cf6' },
    { path: '/trade/history',       label: 'History',        icon: History,      color: '#64748b' },
  ],
  AFFILIATE: [
    { path: '/trade/analytics',     label: 'SLA Dashboard',  icon: BarChart3,    color: '#06b6d4' },
    { path: '/trade/portfolio',     label: 'Link Analytics', icon: TrendingUp,   color: '#10B981' },
    { path: '/trade/earnings',      label: 'Escrow Ledger',  icon: DollarSign,   color: '#E8520F' },
    { path: '/trade/buy-credits',   label: 'Buy Credits',    icon: Zap,          color: '#D4A017' },
    { path: '/trade/history',       label: 'History',        icon: History,      color: '#64748b' },
  ],
  INVESTOR: [
    { path: '/trade/portfolio',     label: 'Telemetry',      icon: TrendingUp,   color: '#E8520F' },
    { path: '/trade/analytics',     label: 'Yield History',  icon: BarChart3,    color: '#D4A017' },
    { path: '/trade/earnings',      label: 'Genesis Invites',icon: DollarSign,   color: '#10B981' },
    { path: '/trade/buy-credits',   label: 'Buy Credits',    icon: Zap,          color: '#D4A017' },
    { path: '/trade/history',       label: 'History',        icon: History,      color: '#64748b' },
  ],
};

const TIER_COLORS = {
  STRATEGY: '#06b6d4',
  HARMONY:  '#f97316',
  POWER:    '#eab308',
};

export default function TradingPortal() {
  const { user, isSubUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [panicMode, setPanicMode] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const tier = user?.tier || licenseInfo?.license?.tier || 'STRATEGY';
  const subRole = (user?.sub_role || 'CLIENT').toUpperCase();
  const tierColor = TIER_COLORS[tier] || '#06b6d4';
  // Read live bot_limit from /api/subuser/license (always live tier config)
  const botLimit = licenseInfo?.license?.bot_limit ?? user?.bot_limit ?? 2;
  const runningBots = licenseInfo?.running_bots ?? 0;
  const TRADE_NAV = NAV_BY_ROLE[subRole] || NAV_BY_ROLE.CLIENT;

  const checkPanic = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/panic/status`, { withCredentials: true });
      setPanicMode(data.panic_stopped);
    } catch {}
  }, []);

  // Fetch earnings + license — role-aware
  const fetchTradingData = useCallback(async () => {
    if (isSubUser) {
      // Sub-user: hit /api/subuser/earnings + /api/subuser/license
      try {
        const [earnRes, licRes] = await Promise.all([
          axios.get(`${API_URL}/api/subuser/earnings`, { withCredentials: true }),
          axios.get(`${API_URL}/api/subuser/license`, { withCredentials: true }),
        ]);
        setEarnings({
          balance: earnRes.data.available_balance || 0,
          total_earned: earnRes.data.total_earned || 0,
        });
        setLicenseInfo(licRes.data);
      } catch (err) {
        // Silent fail — keep last known
      }
    } else {
      // Admin: use admin endpoint
      try {
        const { data } = await axios.get(`${API_URL}/api/bot/earnings`, { withCredentials: true });
        setEarnings(data);
      } catch {}
    }
  }, [isSubUser]);

  useEffect(() => {
    checkPanic();
    fetchTradingData();
    const interval = setInterval(() => { checkPanic(); fetchTradingData(); }, 8000);
    return () => clearInterval(interval);
  }, [checkPanic, fetchTradingData]);

  // Default to volume-bot if on /trade root
  useEffect(() => {
    if (location.pathname === '/trade' || location.pathname === '/trade/') {
      navigate('/trade/volume-bot', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[hsl(220,20%,8%)] flex flex-col">

      {/* ── Top Header ── */}
      <header className="h-14 border-b border-[hsl(220,16%,16%)] bg-[hsl(220,18%,10%)] flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#E8520F]/20 flex items-center justify-center">
              <ShieldAlert className="w-4 h-4 text-[#E8520F]" />
            </div>
            <span className="font-mono font-bold text-white text-sm tracking-wide">GauriLabs</span>
            <span className="font-mono text-[#E8520F] text-sm font-bold">Trading</span>
          </div>

          {/* Tier badge */}
          <div
            className="px-2 py-0.5 rounded text-[10px] font-mono font-bold border"
            style={{ color: tierColor, borderColor: `${tierColor}40`, background: `${tierColor}10` }}
          >
            {tier} TIER
          </div>
          {/* Sub-role badge */}
          <div className="px-2 py-0.5 rounded text-[10px] font-mono font-bold border border-[hsl(220,16%,22%)] text-[hsl(215,16%,56%)]">
            {subRole}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Live earnings */}
          {earnings && (
            <div className="hidden md:flex items-center gap-1.5 text-xs font-mono">
              <Activity className="w-3.5 h-3.5 text-[#10B981]" />
              <span className="text-[#10B981] font-bold">${(earnings.balance || 0).toFixed(2)}</span>
              <span className="text-[hsl(215,16%,45%)]">balance</span>
            </div>
          )}

          {/* Credits */}
          <div className="hidden md:flex items-center gap-1.5 text-xs font-mono">
            <Zap className="w-3.5 h-3.5 text-[#D4A017]" />
            <span className="text-[#D4A017] font-bold">{user?.credits || 0}</span>
            <span className="text-[hsl(215,16%,45%)]">credits</span>
          </div>

          {/* Bot slots — uses live licenseInfo */}
          <div className="hidden md:flex items-center gap-1.5 text-xs font-mono text-[hsl(215,16%,45%)]">
            <Bot className="w-3.5 h-3.5" />
            <span>{runningBots}/{botLimit} bots</span>
          </div>

          {/* User + logout */}
          <div className="flex items-center gap-2">
            <div className="text-right hidden md:block">
              <p className="text-xs font-mono text-white">{user?.name || user?.email?.split('@')[0]}</p>
              <p className="text-[10px] font-mono text-[hsl(215,16%,40%)]">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 rounded hover:bg-[hsl(220,16%,18%)] text-[hsl(215,16%,45%)] hover:text-white transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Panic banner */}
      {panicMode && (
        <div className="bg-red-500/10 border-b border-red-500/30 px-4 py-2 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <p className="text-xs font-mono text-red-400 font-bold">PANIC MODE — All bots are stopped. Contact your administrator.</p>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar ── */}
        <aside className={`${sidebarOpen ? 'w-52' : 'w-14'} shrink-0 border-r border-[hsl(220,16%,16%)] bg-[hsl(220,18%,9%)] flex flex-col transition-all duration-200`}>
          <nav className="flex-1 py-4 space-y-1 px-2">
            {TRADE_NAV.map(({ path, label, icon: Icon, color }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-mono font-medium transition-all ${
                    isActive
                      ? 'bg-[hsl(220,16%,16%)] text-white'
                      : 'text-[hsl(215,16%,50%)] hover:text-white hover:bg-[hsl(220,16%,14%)]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className="w-4 h-4 shrink-0"
                      style={{ color: isActive ? color : undefined }}
                    />
                    {sidebarOpen && (
                      <span style={{ color: isActive ? color : undefined }}>{label}</span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Tier info at bottom of sidebar */}
          {sidebarOpen && (
            <div className="p-3 m-2 rounded-lg border" style={{ borderColor: `${tierColor}30`, background: `${tierColor}08` }}>
              <div className="text-[10px] font-mono text-[hsl(215,16%,45%)] mb-1">YOUR PLAN</div>
              <div className="font-mono font-bold text-sm" style={{ color: tierColor }}>{tier}</div>
              <div className="text-[10px] font-mono text-[hsl(215,16%,45%)] mt-1">{botLimit} bot slots · {user?.credits || 0} credits</div>
            </div>
          )}

          {/* Collapse toggle */}
          <button
            onClick={() => setSidebarOpen(p => !p)}
            className="m-2 p-2 rounded hover:bg-[hsl(220,16%,14%)] text-[hsl(215,16%,40%)] hover:text-white flex items-center justify-center transition-colors"
          >
            <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet context={{ panicMode, tier, botLimit, runningBots, earnings, isSubUser }} />
        </main>
      </div>
    </div>
  );
}
